import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import GestionMoodItemCard from "./GestionMoodItemCard";
import BlocageCreateCard from "./BlocageCreateCard";
import { ICONS } from "../../../utils/fontawesome";
import ReservationDayList from './ReservationDayList'

const isDateInRange = (targetIso, startIso, endIso) => {
  if (!targetIso || !startIso || !endIso) return false;
  const target = new Date(targetIso);
  const start = new Date(startIso);
  const end = new Date(endIso);
  if (
    Number.isNaN(target.getTime()) ||
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime())
  ) {
    return false;
  }
  return target >= start && target <= end;
};

const formatDateFR = (isoDate) => {
  if (!isoDate) return "";
  const dateObj = new Date(isoDate);
  if (Number.isNaN(dateObj.getTime())) return "";
  return dateObj.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "short",
  });
};

const GestionMood = ({
  blockages = [],
  events = [],
  ateliers = [],
  zones = [],
  selectedDay,
  reservations = [],
  onEditBlocage,
  onDeleteBlocage,
  onEditEvenement,
  onDeleteEvenement,
  onCreateIndispo,
}) => {
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [formValues, setFormValues] = useState({
    date_debut: selectedDay || "",
    date_fin: selectedDay || "",
    selectedAteliers: [],
    selectedZones: [],
  });
  const [isFullClosure, setIsFullClosure] = useState(false);

  const filtered = useMemo(() => {
    if (!selectedDay) return blockages;
    return blockages.filter((bloc) =>
      isDateInRange(selectedDay, bloc.date_debut || bloc.start, bloc.date_fin || bloc.end)
    );
  }, [blockages, selectedDay]);

  const filteredEvents = useMemo(() => {
    if (!selectedDay) return events;
    return events.filter((evt) =>
      isDateInRange(selectedDay, evt.date_debut || evt.start, evt.date_fin || evt.end || evt.date)
    );
  }, [events, selectedDay]);

  const filteredReservations = useMemo(() => {
    if (!selectedDay) return [];
    return (Array.isArray(reservations) ? reservations : []).filter((resa) => {
      const date = (resa?.date_atelier || resa?.date || "").slice(0, 10);
      return date === selectedDay;
    });
  }, [reservations, selectedDay]);
  const navigate = useNavigate();

  const formatHoraire = (start, end) => {
    const normalize = (time) => {
      if (!time) return null;
      if (/^\d{2}:\d{2}$/.test(time)) return `${time}:00`;
      if (/^\d{2}:\d{2}:\d{2}$/.test(time)) return time;
      if (/^\d{1,2}$/.test(time)) return `${String(time).padStart(2, "0")}:00:00`;
      return null;
    };
    const label = (time) => {
      if (!time) return "--";
      const [h = "00", m = "00"] = time.split(":");
      return `${h}h${m}`;
    };
    const startNorm = normalize(start);
    const endNorm = normalize(end);
    if (startNorm && endNorm) return `${label(startNorm)} - ${label(endNorm)}`;
    if (startNorm) return `${label(startNorm)}`;
    return "--";
  };

  

  return (
    <div className="bg-white rounded-xl shadow p-4 flex flex-col gap-4 h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3 flex-wrap">
          <h2 className="text-xl font-semibold text-slate-800">
            {selectedDay
              ? `Sélection du ${formatDateFR(selectedDay)}`
              : "Vue globale"}
          </h2>
          {selectedDay && (
            <button
              type="button"
              onClick={() =>
                navigate("/administration", {
                  state: {
                    adminReservation: {
                      section: "reservation",
                      date: selectedDay,
                    },
                  },
                })
              }
              className="text-xs font-semibold text-orange-600 border border-orange-200 rounded-lg px-2.5 py-1 hover:bg-orange-50"
            >
              Voir les réservations
            </button>
          )}
        </div>

      </div>

      <div className="flex flex-col gap-3 overflow-y-auto max-h-[70vh] pr-1">
        <div>
  <div className="flex items-center justify-between mb-1">
    <h3 className="text-sm font-semibold text-slate-700">Réservations du jour</h3>

  </div>
  <ReservationDayList reservations={filteredReservations} />
</div>

        {filtered.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-lg p-4 text-center text-slate-500 text-sm">
            Aucun blocage {selectedDay ? "ce jour" : "enregistré"}.<br />
            {selectedDay ? "Sélectionnez une autre date." : "La liste se mettra à jour ici."}
          </div>
        ) : (
          filtered.map((bloc) => (
            <GestionMoodItemCard
              key={bloc.id || bloc.blocage_id}
              variant="blocage"
              title={bloc.type || bloc.nature || "Blocage"}
              startDate={bloc.date_debut || bloc.start}
              endDate={bloc.date_fin || bloc.end}
              zoneLabel={bloc.zone || bloc.zone_nom}
              atelierLabel={bloc.atelier || bloc.atelier_nom}
              onEdit={onEditBlocage ? () => onEditBlocage(bloc) : undefined}
              onDelete={onDeleteBlocage ? () => onDeleteBlocage(bloc) : undefined}
            />
          ))
        )}

        {filteredEvents.length > 0 && (
          <div className="mt-2 flex flex-col gap-3">
            {filteredEvents.map((evt) => (
              <GestionMoodItemCard
                key={evt.id || evt.evenement_id}
                variant="evenement"
                title={evt.nom || evt.type || "Événement"}
                startDate={evt.date_debut || evt.start || evt.date}
                endDate={evt.date_fin || evt.end}
                zoneLabel={evt.zone || evt.zone_nom}
                atelierLabel={evt.atelier || evt.atelier_nom}
                onEdit={onEditEvenement ? () => onEditEvenement(evt) : undefined}
                onDelete={onDeleteEvenement ? () => onDeleteEvenement(evt) : undefined}
              />
            ))}
          </div>
        )}

        <BlocageCreateCard
          isOpen={isFormOpen}
          onToggle={() => {
            setIsFormOpen((prev) => {
              const next = !prev;
              if (next && onCreateIndispo) onCreateIndispo();
              return next;
            });
          }}
          formValues={formValues}
          setFormValues={setFormValues}
          isFullClosure={isFullClosure}
          setIsFullClosure={setIsFullClosure}
          ateliers={ateliers}
          zones={zones}
        />
      </div>

    </div>
  );
};

export default GestionMood;
